import { NavIcons } from '@bbkAdminComponents/app-layout/nav';
import { type PivotT } from '@bbkAdminComponents/pivot-table/pivot-table';
import { type RestFunnel } from '@bbkAdminRedux/rtkq/funnels.slice';
import { type TrendsDatapoint } from '@bbkAdminRedux/rtkq/reports.slice';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { type RuleMetadata } from '@bbkAdminRedux/rtkq/rules.slice';
import { NumberKit } from '@bbkAdminUtils/number-kit';
import { type Dictionary } from '@bbkAdminUtils/utility-types';

export const EXPERIENCE_PERF_TABLE_NAME =
  'ChargebeeRetention_Experience_Performance';

export const DEFAULT_EXPERIENCE_PERF_GROUP_BY = ['experience', 'reason'];

export type ExperiencePerformanceRow = TrendsDatapoint;

export type ExperiencePerformancePivotRowDefinition = {
  name: string;
  field: string[];
  label: string;
  icon?: React.ReactNode | null;
};

type ExperiencePerformanceCalculatedFieldsDeps = {
  rulesMap: Dictionary<RuleMetadata>;
  vanityReasonsMap: Dictionary<{ display_name?: string }>;
  funnelsMap: Dictionary<RestFunnel>;
  selectedBrandLabel?: string;
};

type ExperiencePerformanceReportTags = {
  funnelId: string;
  audience1Id: string;
  audience2Id?: string;
  audience3Id?: string;
  audience4Id?: string;
  reasonId: string;
};

const EXPERIENCE_PERF_PIVOT_ROW_ICONS: Record<string, React.ReactNode> = {
  audience: NavIcons.audiences,
  experience: NavIcons.experiences,
  reason: NavIcons.reasons,
};

export const filterExperienceTrendsRows = (
  trends: TrendsDatapoint[]
): TrendsDatapoint[] =>
  trends.filter(
    (t) =>
      t.is_timeperiod_stub === 0 &&
      t.timeGroup !== null &&
      (t.canceled > 0 || t.deflects > 0)
  );

export const createExperiencePerformanceCalculatedFields = ({
  rulesMap,
  vanityReasonsMap,
  funnelsMap,
  selectedBrandLabel,
}: ExperiencePerformanceCalculatedFieldsDeps) => {
  const parseFunnelAudienceContent = (
    row: ExperiencePerformanceRow
  ): ExperiencePerformanceReportTags => {
    const tags = row.displayGroupId.split(',');

    const funnelIdIdx = 0;
    const funnelId = tags.at(funnelIdIdx)!;

    const reasonIdIdx = -1;
    const reasonId = tags.at(reasonIdIdx)!;

    const audience1IdIdx = 1;
    const audienceIds = tags.slice(audience1IdIdx, reasonIdIdx);
    const audience1Id = audienceIds.shift()!;
    const audience2Id = audienceIds.shift();
    const audience3Id = audienceIds.shift();
    const audience4Id = audienceIds.shift();

    return {
      funnelId,
      audience1Id,
      audience2Id,
      audience3Id,
      audience4Id,
      reasonId,
    };
  };

  const getAudienceDisplayName = (
    audId: keyof ExperiencePerformanceReportTags,
    row: ExperiencePerformanceRow
  ) => {
    const reportTags = parseFunnelAudienceContent(row);
    const audienceId = reportTags[audId];

    if (audienceId === 'control') return 'Control';
    if (audienceId === 'fallback') return 'Fallback';

    if (audienceId) {
      return rulesMap[audienceId]?.display_name ?? `Audience ${audienceId}`;
    }
  };

  return ((row) => {
    return {
      vanityReasonName: () => {
        if (row.displayGroupId === 'None') return 'Unknown reason';

        const reportTags = parseFunnelAudienceContent(row);
        const vanityReasonId = reportTags.reasonId;

        if (vanityReasonId) {
          return (
            vanityReasonsMap[vanityReasonId]?.display_name ??
            `Reason ${vanityReasonId}`
          );
        }

        return 'No reason selected';
      },
      funnelDisplayName: () => {
        if (row.displayGroupId === 'None') return 'Unknown funnel';

        const reportTags = parseFunnelAudienceContent(row);
        const funnelId = reportTags.funnelId;

        if (funnelId) {
          return funnelsMap[funnelId]?.display_name ?? `Funnel ${funnelId}`;
        }

        return 'Unknown funnel';
      },
      audience1DisplayName: () => {
        if (row.displayGroupId === 'None') return 'Fallback';

        const audienceDisplayName = getAudienceDisplayName('audience1Id', row);

        return audienceDisplayName ?? 'Unknown audience';
      },
      audience2DisplayName: () => {
        return getAudienceDisplayName('audience2Id', row);
      },
      audience3DisplayName: () => {
        return getAudienceDisplayName('audience3Id', row);
      },
      audience4DisplayName: () => {
        return getAudienceDisplayName('audience4Id', row);
      },
      brandName: () => {
        if (row.displayGroupId === 'None') return 'Default';

        if (selectedBrandLabel) {
          return selectedBrandLabel;
        }

        return 'Unknown brand';
      },
      totalSessions: () => row.deflects + row.direct_canceled,
      deflectRate: () => {
        const totalSessions = row.deflects + row.direct_canceled;
        return (row.deflects / totalSessions) * 100;
      },
      lostDeflects: () => row.deflects - (row.cohort_saved + row.watchlists),
      saveRate: () =>
        (row.cohort_saved / (row.cohort_canceled + row.cohort_saved)) * 100,
      modalOfferAcceptRate: () =>
        (row.modal_offer_accepts / row.modal_offer_views) * 100,
      laOfferAcceptsRate: () =>
        (row.la_offer_accepts / row.la_offer_views) * 100,
      totalOfferAccepts: () => row.la_offer_accepts + row.modal_offer_accepts,
      passiveDeflects: () => row.deflects - row.offer_accept_deflects,
      passiveDeflectRate: () => {
        const totalSessions = row.deflects + row.direct_canceled;
        const passiveDeflects = row.deflects - row.offer_accept_deflects;

        return (passiveDeflects / totalSessions) * 100;
      },
      firstTimeRepurchaseRate: () =>
        (row.deflect_payment_sessions / row.deflects) * 100,
      avgNumRepurchaseOrders: () =>
        row.deflect_payments_count / row.deflect_payment_sessions,
      avgValRepurchaseOrders: () =>
        row.deflect_payment_revenue / row.deflect_payments_count,
      winbackRate: () => {
        const lostDeflects = row.deflects - (row.cohort_saved + row.watchlists);
        return (
          (row.cancel_payment_sessions / (row.direct_canceled + lostDeflects)) *
          100
        );
      },
      avgNumWinbackOrders: () =>
        row.cancel_payments_count / row.cancel_payment_sessions,
      avgValWinbackOrders: () =>
        row.cancel_payment_revenue / row.cancel_payments_count,
      activeDeflectRate: () =>
        row.deflects ? (row.deflect_still_paying / row.deflects) * 100 : 0,
    };
  }) satisfies PivotT.CalculatedFields<ExperiencePerformanceRow>;
};

export const getExperiencePerformancePivotRowDefinitions = (
  isNestedAudiencesEnabled: boolean
): ExperiencePerformancePivotRowDefinition[] => {
  const result: ExperiencePerformancePivotRowDefinition[] = [
    {
      field: ['audience1DisplayName'],
      icon: null,
      name: 'audience',
      label: 'Audiences',
    },
    {
      field: ['funnelDisplayName'],
      icon: null,
      name: 'experience',
      label: 'Experiences',
    },
    {
      field: ['vanityReasonName'],
      icon: null,
      name: 'reason',
      label: 'Reasons',
    },
  ];

  if (isNestedAudiencesEnabled) {
    result[0]!.field = [
      'audience1DisplayName',
      'audience2DisplayName',
      'audience3DisplayName',
      'audience4DisplayName',
    ];
  }

  return result;
};

type ExperiencePerformancePivotRow = PivotT.ComputedFields<
  ExperiencePerformanceRow,
  ReturnType<typeof createExperiencePerformanceCalculatedFields>
>;

type ExperiencePerformancePivotValues =
  PivotT.PivotValue<ExperiencePerformancePivotRow>[];

const genericExperiencePerformancePivotValues: ExperiencePerformancePivotValues =
  [
    {
      label: 'Total sessions',
      field: 'totalSessions',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Canceled',
      field: 'direct_canceled',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Watchlist',
      field: 'watchlists',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Saved',
      field: 'cohort_saved',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Deflect rate',
      field: 'deflectRate',
      format: NumberKit.formatPercent,
    },
    {
      label: 'Lost Deflects',
      field: 'lostDeflects',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Save rate',
      field: 'saveRate',
      format: NumberKit.formatPercent,
    },
    {
      label: 'Modal offer accepts',
      field: 'modal_offer_accepts',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Modal offer views',
      field: 'modal_offer_views',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Modal offer accept rate',
      field: 'modalOfferAcceptRate',
      format: NumberKit.formatPercent,
    },
    {
      label: 'LA offer accepts',
      field: 'la_offer_accepts',
      format: NumberKit.formatNumber,
    },
    {
      label: 'LA offer views',
      field: 'la_offer_views',
      format: NumberKit.formatNumber,
    },
    {
      label: 'LA offer accept rate',
      field: 'laOfferAcceptsRate',
      format: NumberKit.formatPercent,
    },
    {
      label: 'Total offer accepts',
      field: 'totalOfferAccepts',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Passive deflects',
      field: 'passiveDeflects',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Deflects',
      field: 'deflects',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Active deflects',
      field: 'offer_accept_deflects',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Passive deflects rate',
      field: 'passiveDeflectRate',
      format: NumberKit.formatPercent,
    },
  ];

const repurchaseExperiencePerformancePivotValues: ExperiencePerformancePivotValues =
  [
    {
      label: 'First repurchase order',
      field: 'deflect_payment_sessions',
      format: NumberKit.formatNumber,
    },
    {
      label: 'First time repurchase rate',
      field: 'firstTimeRepurchaseRate',
      format: NumberKit.formatPercent,
    },
    {
      label: 'Cumulative retained revenue',
      field: 'deflect_payment_revenue',
      format: NumberKit.formatMoney,
    },
    {
      label: 'Total # of repurchase orders',
      field: 'deflect_payments_count',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Avg. # of repurchase orders',
      field: 'avgNumRepurchaseOrders',
      format: NumberKit.formatNumberPrecision1,
    },
    {
      label: 'Avg. value of repurchase orders',
      field: 'avgValRepurchaseOrders',
      format: NumberKit.formatMoneyPrecision1,
    },
    {
      label: 'First winback orders',
      field: 'cancel_payment_sessions',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Winback rate',
      field: 'winbackRate',
      format: NumberKit.formatPercent,
    },
    {
      label: 'Cumulative winback revenue',
      field: 'cancel_payment_revenue',
      format: NumberKit.formatMoney,
    },
    {
      label: 'Total # of winback orders',
      field: 'cancel_payments_count',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Avg. # of winback orders',
      field: 'avgNumWinbackOrders',
      format: NumberKit.formatNumberPrecision1,
    },
    {
      label: 'Avg. value of winback orders',
      field: 'avgValWinbackOrders',
      format: NumberKit.formatMoneyPrecision1,
    },
    {
      label: '% of cohort still paying',
      field: 'activeDeflectRate',
      format: NumberKit.formatPercent,
    },
  ];

export const getExperiencePerformancePivotValues = (
  isRepurchaseAttributionEnabled?: boolean
): ExperiencePerformancePivotValues =>
  isRepurchaseAttributionEnabled
    ? [
        ...genericExperiencePerformancePivotValues,
        ...repurchaseExperiencePerformancePivotValues,
      ]
    : genericExperiencePerformancePivotValues;

export const getExperiencePerformanceVisibleColumns = (): string[] => [
  'totalSessions',
  'direct_canceled',
  'watchlists',
  'cohort_saved',
  'deflectRate',
  'lostDeflects',
  'saveRate',
];

export const resolveExperiencePerformancePivotRows = (
  groupByOpts: string[],
  isNestedAudiencesEnabled: boolean
): PivotT.GroupFieldLoose<ExperiencePerformancePivotRow>[] =>
  groupByOpts
    .map((groupBy) =>
      getExperiencePerformancePivotRowDefinitions(
        isNestedAudiencesEnabled
      ).find((row) => row.name === groupBy)
    )
    .filter(isDefined)
    .map((row) => ({
      ...row,
      icon: EXPERIENCE_PERF_PIVOT_ROW_ICONS[row.name] ?? null,
    }));
